/**
 * The schematic canvas: pointer and keyboard events in, reducer actions out.
 *
 * This component holds no editing rules. It translates gestures into
 * `EditorAction`s (reducer.ts), resolves what a pointer is over
 * (interaction.ts), and draws the scene with the one SVG renderer. What it
 * keeps locally is only what a gesture is in the middle of: a press that has
 * not yet become a drag, the rubber band, a pan.
 *
 * Interaction follows D21: drag from the palette to place, a connector tool
 * dragged port to port with valid targets highlighted, click, shift-click and
 * box select, drag to move, pan and zoom, and a context menu. Every one of
 * those has a keyboard path (requirement 12.7); `interpretKey` is the map.
 *
 * Coordinates: pointer events arrive in client pixels, become canvas pixels
 * by subtracting the canvas's position, and become scene units through the
 * viewport. Nothing below the viewport conversion ever sees a screen pixel,
 * and nothing in the viewport ever touches the document (requirement 12.8).
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import type { Finding } from "../design/document";
import { heightFor, NOTE_METRICS } from "../scene/notes";
import { labelPlacement, portStatusAt, SvgRenderer } from "../render";
import {
  buildScene,
  createSceneBuilder,
  type Point,
  type Scene,
  type SceneEdge,
} from "../scene";
import { moveSegment, segmentHandles, type SegmentHandle } from "./bends";
import { commandsFor, type CommandContext, type EditorHost } from "./commands";
import type { MenuEntry } from "./ContextMenu";
import { ContextMenu } from "./ContextMenu";
import { NoteEditor, type NoteEditorHandle } from "./NoteEditor";
import {
  boxBetween,
  DRAG_THRESHOLD,
  drawnPorts,
  edgeAt,
  interpretKey,
  menuAnchor,
  PORT_TOLERANCE,
  portAt,
  readingOrder,
  selectableOf,
  targetOfElement,
  type Target,
  type ViewCommand,
} from "./interaction";
import { connectionAt, nextFreePort, reconnectTargets, type EdgeEnd } from "./rules";
import { sceneViewFor } from "./sceneView";
import { useDesignLiquidUnits } from "../workspace/DisplayUnitsContext";
import { effectiveParameters, validateParameter } from "../workspace/parameters";
import { formatNumber, fromCanonical, parseQuantity, type DisplayUnit } from "../workspace/units";
import { lengthLabelUnit } from "../scene";
import type { EditorAction, EditorState } from "./state";
import type { StepPreview } from "../scripts/preview";

/** Something the preview edits in place on the drawing. */
export interface PreviewEdit {
  kind: "port" | "bag" | "pump" | "regulator";
  ref: string;
}

/** What a read-only canvas lets through: looking, selecting, and moving the view. */
const VIEW_ONLY: ReadonlySet<EditorAction["type"]> = new Set([
  "SELECT",
  "SELECT_ALL",
  "CLEAR_SELECTION",
  "BOX_SELECT",
  "SET_VIEWPORT",
  "SET_ANNOTATIONS",
  "SET_COLLAPSED",
  "CANCEL",
  "CLEAR_NOTICE",
  "COPY_SELECTION",
]);
import { fitToBounds, panBy, sceneToScreen, screenToScene, zoomAbout } from "./viewport";

/**
 * The in-place label editor: an input over the label, sized to its text.
 * Enter or leaving it commits, Escape cancels. An empty label is allowed, and
 * shows the identifier instead, as an unlabelled component always has.
 */
function LabelEditor({
  initial,
  placeholder,
  x,
  y,
  zoom,
  onCommit,
  onCancel,
}: {
  initial: string;
  placeholder: string;
  x: number;
  y: number;
  zoom: number;
  onCommit: (label: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  const finish = (commit: boolean) => {
    if (done.current) return;
    done.current = true;
    if (commit) onCommit(value);
    else onCancel();
  };
  const fontSize = Math.max(10, 11 * zoom);
  const width = Math.max(96, (Math.max(value.length, placeholder.length) + 2) * fontSize * 0.6);
  return (
    <input
      ref={inputRef}
      data-label-editor=""
      className="cx-label-editor"
      aria-label={`Label for ${placeholder}`}
      value={value}
      placeholder={placeholder}
      maxLength={120}
      style={{ left: x - width / 2, top: y - fontSize - 3, width, fontSize }}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          finish(true);
        } else if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          finish(false);
        }
      }}
      onBlur={() => finish(true)}
    />
  );
}

/**
 * A tube's length, edited where its dimension reads (double-click the label).
 * Typed in the unit shown, or naming its own ("25 cm"); a value that cannot be
 * stored keeps the field open and says why, as the inspector does.
 */
function LengthEditor({
  edgeRef,
  initial,
  unit,
  x,
  y,
  zoom,
  check,
  onCommit,
  onCancel,
}: {
  edgeRef: string;
  initial: number | null;
  unit: DisplayUnit;
  x: number;
  y: number;
  zoom: number;
  /** Why a length (metre) cannot be stored, or null. */
  check: (metres: number) => string | null;
  /** Called only for a changed, valid length (metre). */
  onCommit: (metres: number) => void;
  onCancel: () => void;
}) {
  const shown = initial === null ? "" : `${formatNumber(fromCanonical(initial, unit))} ${unit.label}`;
  const [value, setValue] = useState(shown);
  const [problem, setProblem] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    // The number selected, the unit kept: typing replaces just the number.
    const number = shown.lastIndexOf(" ");
    input.setSelectionRange(0, number > 0 ? number : shown.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const finish = (commit: boolean) => {
    if (done.current) return;
    if (!commit || value.trim() === shown.trim()) {
      done.current = true;
      onCancel();
      return;
    }
    const parsed = parseQuantity(value, "meter", unit);
    const reason = parsed.ok ? check(parsed.value) : parsed.reason;
    if (reason || !parsed.ok) {
      setProblem(reason);
      inputRef.current?.focus();
      return;
    }
    done.current = true;
    onCommit(parsed.value);
  };
  const fontSize = Math.max(10, 11 * zoom);
  const width = Math.max(80, (value.length + 3) * fontSize * 0.6);
  const top = y - fontSize / 2 - 3;
  return (
    <>
      <input
        ref={inputRef}
        data-label-editor=""
        className="cx-label-editor"
        aria-label={`Length of ${edgeRef}`}
        aria-invalid={problem ? true : undefined}
        title={problem ?? `Length of ${edgeRef}: a number in ${unit.label}, or with its own unit`}
        value={value}
        placeholder={unit.label}
        aria-describedby={problem ? `${edgeRef}-length-problem` : undefined}
        style={{ left: x - width / 2, top, width, fontSize }}
        onChange={(event) => {
          setValue(event.target.value);
          setProblem(null);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            finish(true);
          } else if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            finish(false);
          }
        }}
        onBlur={() => {
          // A refused value is dropped when focus leaves: the label keeps the stored length.
          if (problem === null) finish(true);
          if (!done.current) {
            done.current = true;
            onCancel();
          }
        }}
      />
      {problem && (
        <div
          id={`${edgeRef}-length-problem`}
          role="alert"
          className="cx-length-problem"
          style={{ left: x, top: top + fontSize + 10 }}
        >
          {problem}
        </div>
      )}
    </>
  );
}

/** Screen pixels within which a dropped component lands on a line. */
const DROP_TOLERANCE = 10;

/** The drag-and-drop type a palette tile carries: a template version id. */
export const TEMPLATE_DRAG_TYPE = "application/x-fluidea-template";

/** How long a notice about the last action stays on the canvas. */
export const CANVAS_NOTICE_MS = 8000;
/**
 * A block definition version dragged from the palette. Placing one is a server
 * operation (it allocates the members' identifiers and records the version),
 * so the drop goes to the host rather than to the reducer.
 */
export const BLOCK_DRAG_TYPE = "application/x-fluidea-block";

export interface EditorCanvasProps {
  state: EditorState;
  dispatch: (action: EditorAction) => void;
  width: number;
  height: number;
  findings?: readonly Finding[];
  host?: EditorHost;
  title?: string;
  /**
   * Whether notes can be written here. A design's canvas: yes. A block
   * definition's: no, its document has nowhere to keep them.
   */
  notes?: boolean;
  /** A script step's connectivity preview, drawn over the schematic (D42). */
  preview?: StepPreview | null;
  /**
   * Look, select and pan, but change nothing: while a script is previewed,
   * the drawing is what the script runs on.
   */
  readOnly?: boolean;
  /** Read-only, undo (false) and redo (true) of what is edited instead, such as a script. */
  onHistory?: (redo: boolean) => void;
  /** The context menu's commands, in place of the editor's: the Scripts step has its own. */
  menuCommands?: (target: Target, context: CommandContext) => MenuEntry[];
  /** A click on a state badge in the preview: move that component to its next state. */
  onToggleState?: (ref: string) => void;
  /** What the preview is editing in place: a port's status or a bag's contents. Controlled. */
  previewEdit?: PreviewEdit | null;
  /** A click on a port's status or a bag in the preview, or (null) the editor closing. */
  onPreviewEdit?: (edit: PreviewEdit | null) => void;
  /** The in-place editor for `edit`, at a screen point: the host's, which knows what it edits. */
  renderPreviewEdit?: (edit: PreviewEdit, at: Point, close: () => void) => ReactNode;
  /** Components and lines to pick out, such as a hovered bill of materials line's. */
  spotlight?: ReadonlySet<string>;
}

type Gesture =
  | { kind: "press"; start: Point; startScene: Point; onElement: boolean; shift: boolean }
  | { kind: "move"; startScene: Point }
  | { kind: "box"; startScene: Point; shift: boolean }
  | { kind: "pan"; last: Point }
  | { kind: "connect" }
  /** The edge as it was when the drag began: offsets apply to it, never compound. */
  | { kind: "segment"; edge: SceneEdge; handle: SegmentHandle; startScene: Point }
  | { kind: "note-size"; ref: string; startScene: Point; start: { width: number; height: number } };

interface OpenMenu {
  x: number;
  y: number;
  target: Target;
}

const NO_FINDINGS: readonly Finding[] = [];
const NO_HOST: EditorHost = {};

export function EditorCanvas({
  state,
  width,
  height,
  findings = NO_FINDINGS,
  host = NO_HOST,
  title = "Schematic",
  notes = false,
  preview: stepPreview = null,
  readOnly = false,
  onHistory,
  menuCommands = commandsFor,
  onToggleState,
  previewEdit = null,
  onPreviewEdit,
  spotlight,
  renderPreviewEdit,
  dispatch: dispatchAny,
}: EditorCanvasProps) {
  // Read-only: only what looks, selects or moves the view gets through.
  const dispatch = useMemo(
    () =>
      readOnly
        ? (action: EditorAction) => {
            if (action.type === "UNDO" || action.type === "REDO") return onHistory?.(action.type === "REDO");
            return VIEW_ONLY.has(action.type) && dispatchAny(action);
          }
        : dispatchAny,
    [readOnly, dispatchAny, onHistory],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const builderRef = useRef(createSceneBuilder());
  const gestureRef = useRef<Gesture | null>(null);
  const spaceHeld = useRef(false);
  const [band, setBand] = useState<{ a: Point; b: Point } | null>(null);
  const [pointer, setPointer] = useState<Point | null>(null);
  const [portCursor, setPortCursor] = useState<string | null>(null);
  /** An edge end being dragged to another port, with where it may go. */
  const [reconnect, setReconnect] = useState<{
    edge: string;
    end: EdgeEnd;
    targets: ReadonlySet<string>;
    hover: string | null;
  } | null>(null);
  /** The component whose label is being edited in place (double-click or F2). */
  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const [editingLength, setEditingLength] = useState<string | null>(null);
  /** A note being written: an existing one, or a new one at a point. */
  const noteHelpId = useId();
  const noteEditorRef = useRef<NoteEditorHandle>(null);
  const [editingNote, setEditingNote] = useState<
    { ref: string } | { at: Point; anchor: string | null } | null
  >(null);
  /** The line a component being dragged in from the palette would be spliced into. */
  const [dropEdge, setDropEdge] = useState<string | null>(null);
  /** The segment of the selected edge the arrow keys move, chosen with B. */
  const [segmentCursor, setSegmentCursor] = useState<number | null>(null);
  const [menu, setMenu] = useState<OpenMenu | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const instructionsId = useId();

  // A notice about the last action goes by itself after a while, as well as
  // on the next action. A refusal inside a locked block stays: the workspace
  // offers the way out beside it, with its own close (requirement 8.9).
  const notice = state.notice;
  useEffect(() => {
    if (!notice || (notice.kind === "refused" && notice.code === "locked_block_member")) return;
    const timer = window.setTimeout(() => dispatch({ type: "CLEAR_NOTICE" }), CANVAS_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [notice, dispatch]);

  const liquidUnits = useDesignLiquidUnits();
  const view = useMemo(
    () =>
      sceneViewFor({
        collapsed: state.collapsed,
        grid: state.grid,
        annotations: state.annotations,
        designKind: state.designKind,
      }, stepPreview, liquidUnits),
    // By field, not by `state.grid`: snapping does not change the drawing.
    [state.collapsed, state.grid.spacing, state.grid.visible, state.annotations, state.designKind, stepPreview, liquidUnits],
  );
  const scene: Scene = builderRef.current.build(state.document, state.library, findings, view);
  // The note being written is the editor, drawn over where its card was.
  const editingRef = editingNote && "ref" in editingNote ? editingNote.ref : null;
  const drawnScene = useMemo(
    () => (editingRef ? { ...scene, notes: scene.notes.filter((note) => note.ref !== editingRef) } : scene),
    [scene, editingRef],
  );
  // The resize handle: on the one selected note, while not being written.
  const sizingNote =
    state.tool === "select" && state.selection.size === 1 && !editingNote
      ? scene.notes.find((note) => state.selection.has(note.ref))
      : undefined;
  // While a component is dragged, lines reroute around it, so hit-testing
  // them as drawn would make a line dodge whatever is dropped on it. Lines
  // are tested as they were when the drag began.
  const moveOrigin = state.drag?.kind === "move" ? state.drag.origin : null;
  const originScene = useMemo(
    () => (moveOrigin ? buildScene(moveOrigin, state.library, [], view) : null),
    [moveOrigin, state.library, view],
  );
  /**
   * The one component being moved, if it could be spliced into a line: a
   * single free-standing node, not in a block, with no lines of its own.
   */
  const spliceCandidate = (): string | null => {
    if (state.selection.size !== 1) return null;
    const ref = [...state.selection][0]!;
    const node = state.document.nodes?.find((candidate) => candidate.local_id === ref);
    // A mounted part slides along its tube; it is never spliced into another.
    if (!node || node.group_instance || node.mount) return null;
    const connected = (state.document.edges ?? []).some(
      (edge) => edge.from_port.split(".")[0] === ref || edge.to_port.split(".")[0] === ref,
    );
    return connected ? null : ref;
  };
  const { viewport } = state;

  // Handlers read the latest state through refs, so pointer listeners do not
  // have to be rebound on every render during a drag.
  const latest = useRef({ state, scene });
  latest.current = { state, scene };

  const local = (event: { clientX: number; clientY: number }): Point => {
    const box = containerRef.current?.getBoundingClientRect();
    return { x: event.clientX - (box?.left ?? 0), y: event.clientY - (box?.top ?? 0) };
  };

  const setViewport = useCallback(
    (next: EditorState["viewport"]) => dispatch({ type: "SET_VIEWPORT", viewport: next }),
    [dispatch],
  );

  // The one selected edge, when bends can be adjusted: select tool, nothing else selected.
  const editableEdge =
    state.tool === "select" && state.selection.size === 1 && state.drag?.kind !== "connect"
      ? (scene.edges.find((edge) => state.selection.has(edge.ref)) ?? null)
      : null;
  const handles = editableEdge ? segmentHandles(editableEdge) : [];
  const snapValue = useCallback(
    (value: number) => (state.grid.snap ? Math.round(value / state.grid.spacing) * state.grid.spacing : value),
    [state.grid.snap, state.grid.spacing],
  );

  // A segment cursor belongs to one edge; selecting anything else drops it.
  useEffect(() => {
    if (segmentCursor !== null && !handles.some((handle) => handle.index === segmentCursor)) setSegmentCursor(null);
  }, [handles, segmentCursor]);

  // The ports P steps through: a selected line's two ends, or the selected
  // component's ports.
  const ownedPorts = useMemo(() => {
    const owner = [...state.selection][0];
    if (!owner || state.selection.size !== 1) return [];
    const ownerEdge = scene.edges.find((edge) => edge.ref === owner);
    if (ownerEdge) return [ownerEdge.from, ownerEdge.to].filter((end) => !end.concealed).map((end) => end.ref);
    return [
      ...(scene.nodes.find((node) => node.ref === owner)?.ports ?? []),
      ...(scene.blocks.find((block) => block.ref === owner)?.ports ?? []),
    ].map((port) => port.ref);
  }, [scene, state.selection]);

  // A highlighted port belongs to what is selected: selecting something else,
  // or the port no longer being drawn, ends port choosing.
  useEffect(() => {
    if (portCursor && !ownedPorts.includes(portCursor)) setPortCursor(null);
  }, [ownedPorts, portCursor]);

  /** Leave port choosing (P) and segment choosing (B). */
  const clearCursors = useCallback(() => {
    setPortCursor(null);
    setSegmentCursor(null);
  }, []);

  // Refusals and removals are spoken as well as shown (requirements 6.3, 6.6).
  useEffect(() => {
    if (state.notice) setAnnouncement(state.notice.message);
  }, [state.notice]);

  // Wheel: pans, or zooms about the pointer with Ctrl/Cmd (and trackpad pinch,
  // which browsers report as a Ctrl wheel). Bound natively because React's
  // wheel listener is passive and cannot stop the page scrolling.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const current = latest.current.state.viewport;
      if (event.ctrlKey || event.metaKey) {
        // A trackpad pinch sends many small deltas; a mouse notch sends one
        // large one (~100). Capping each event keeps a notch to a step of about
        // 1.6x rather than a leap to the zoom limit, without slowing pinch.
        const delta = Math.max(-50, Math.min(50, event.deltaY));
        setViewport(zoomAbout(current, Math.exp(-delta * 0.01), local(event)));
      } else {
        setViewport(panBy(current, -event.deltaX, -event.deltaY));
      }
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  }, [setViewport]);

  // --- Selection helpers ------------------------------------------------------

  const describe = useCallback((ref: string): string => {
    const { scene: current } = latest.current;
    const node = current.nodes.find((candidate) => candidate.ref === ref);
    if (node) return node.label === ref ? `${node.role.replace(/_/g, " ")} ${ref}` : `${node.label}, ${ref}`;
    const edge = current.edges.find((candidate) => candidate.ref === ref);
    if (edge) return `${edge.type.replace(/_/g, " ")} ${ref}${edge.lengthLabel ? `, ${edge.lengthLabel}` : ""}`;
    const block = current.blocks.find((candidate) => candidate.ref === ref);
    return block ? `block ${block.name}, ${ref}` : ref;
  }, []);

  /** Pan just enough to bring a scene point into view, for keyboard selection. */
  const ensureVisible = useCallback(
    (point: Point | null) => {
      if (!point) return;
      const current = latest.current.state.viewport;
      const screen = sceneToScreen(current, point);
      const margin = 48;
      let dx = 0;
      let dy = 0;
      if (screen.x < margin) dx = margin - screen.x;
      else if (screen.x > width - margin) dx = width - margin - screen.x;
      if (screen.y < margin) dy = margin - screen.y;
      else if (screen.y > height - margin) dy = height - margin - screen.y;
      if (dx || dy) setViewport(panBy(current, dx, dy));
    },
    [width, height, setViewport],
  );

  const connectFrom = useCallback(
    (port: string) => {
      if (latest.current.state.tool !== "connect") dispatch({ type: "SET_TOOL", tool: "connect" });
      dispatch({ type: "BEGIN_CONNECT", port });
      setPortCursor(null);
    },
    [dispatch],
  );

  // --- Menus ------------------------------------------------------------------

  const openMenu = useCallback((x: number, y: number, target: Target) => setMenu({ x, y, target }), []);

  const kindOf = (ref: string): Target["kind"] => {
    if (scene.notes.some((note) => note.ref === ref)) return "note";
    if (scene.nodes.some((node) => node.ref === ref)) return "node";
    if (scene.blocks.some((block) => block.ref === ref)) return "block";
    if (scene.edges.some((edge) => edge.ref === ref)) return "edge";
    return "canvas";
  };

  const openMenuFromKeyboard = () => {
    const ref = portCursor ?? [...state.selection][0] ?? null;
    const box = containerRef.current?.getBoundingClientRect();
    const port = ref ? drawnPorts(scene).find((candidate) => candidate.ref === ref) : undefined;
    const anchor = port ? port.position : menuAnchor(scene, ref);
    const at = anchor ? sceneToScreen(viewport, anchor) : { x: 24, y: 24 };
    const target: Target = port
      ? { kind: "port", ref: port.ref }
      : ref
        ? { kind: kindOf(ref), ref }
        : { kind: "canvas", ref: null };
    openMenu((box?.left ?? 0) + at.x, (box?.top ?? 0) + at.y, target);
  };

  // --- View commands ----------------------------------------------------------

  const runView = (command: ViewCommand): boolean => {
    const centre = { x: width / 2, y: height / 2 };
    switch (command.kind) {
      case "zoom":
        setViewport(zoomAbout(viewport, command.factor, centre));
        return true;
      case "zoom-reset":
        setViewport(zoomAbout(viewport, 1 / viewport.zoom, centre));
        return true;
      case "fit":
        setViewport(fitToBounds(scene.bounds, width, height));
        return true;
      case "pan":
        setViewport(panBy(viewport, command.dx, command.dy));
        return true;
      case "menu":
        openMenuFromKeyboard();
        return true;
      case "cycle-selection": {
        const order = readingOrder(scene);
        if (order.length === 0) return false;
        const current = order.findIndex((ref) => state.selection.has(ref));
        const next = current === -1 ? (command.step > 0 ? 0 : order.length - 1) : current + command.step;
        // Past either end, Tab leaves the canvas: never a keyboard trap.
        if (next < 0 || next >= order.length) return false;
        const ref = order[next]!;
        dispatch({ type: "SELECT", refs: [ref] });
        setPortCursor(null);
        setAnnouncement(`Selected ${describe(ref)}`);
        ensureVisible(menuAnchor(scene, ref));
        return true;
      }
      case "cycle-port": {
        if (state.drag?.kind === "connect") {
          // Valid targets, nearest first, so P walks outwards from the start.
          const from = drawnPorts(scene).find((port) => port.ref === (state.drag?.kind === "connect" ? state.drag.from : ""));
          const targets = drawnPorts(scene)
            .filter((port) => state.drag?.kind === "connect" && state.drag.validTargets.has(port.ref))
            .sort((a, b) =>
              from
                ? Math.hypot(a.position.x - from.position.x, a.position.y - from.position.y) -
                    Math.hypot(b.position.x - from.position.x, b.position.y - from.position.y)
                : a.ref.localeCompare(b.ref),
            )
            .map((port) => port.ref);
          if (targets.length === 0) {
            setAnnouncement("No port can take this connection");
            return true;
          }
          const at = state.drag.hover ? targets.indexOf(state.drag.hover) : -1;
          const next = targets[(at + command.step + targets.length) % targets.length]!;
          dispatch({ type: "HOVER_PORT", port: next });
          setAnnouncement(`Connect to ${next}`);
          return true;
        }
        // A selected line: P steps between its two ends, so Tab can then move one.
        const ports = ownedPorts;
        if (ports.length === 0) {
          setAnnouncement("Select a component to choose one of its ports");
          return true;
        }
        const at = portCursor ? ports.indexOf(portCursor) : -1;
        const next = ports[(at + command.step + ports.length) % ports.length]!;
        setPortCursor(next);
        const holder = connectionAt(state.document, next);
        setAnnouncement(
          holder
            ? `Port ${next}, connected by ${holder.edge}. Tab moves the connection to the next free port. Escape finishes.`
            : `Port ${next}. Enter connects from it. Escape finishes.`,
        );
        return true;
      }
      case "cycle-segment": {
        if (!editableEdge || handles.length === 0) {
          setAnnouncement("Select a channel to adjust its bends");
          return true;
        }
        const at = segmentCursor === null ? -1 : handles.findIndex((handle) => handle.index === segmentCursor);
        const next = handles[(at + command.step + handles.length) % handles.length]!;
        setSegmentCursor(next.index);
        setAnnouncement(
          `Segment ${handles.indexOf(next) + 1} of ${handles.length}, ${next.orientation}. ` +
            (next.orientation === "vertical" ? "Left and right arrows move it." : "Up and down arrows move it.") +
            " Escape finishes.",
        );
        return true;
      }
      case "port-enter":
        if (state.drag?.kind === "connect") {
          dispatch({ type: "COMPLETE_CONNECT" });
          return true;
        }
        if (portCursor) {
          connectFrom(portCursor);
          setAnnouncement(`Connecting from ${portCursor}. P chooses a target, Enter connects, Escape cancels.`);
          return true;
        }
        return false;
      case "inspect": {
        const ref = [...state.selection][0];
        if (ref && host.inspect) host.inspect(ref);
        return Boolean(ref && host.inspect);
      }
      case "edit-label": {
        if (readOnly) return false;
        const ref = [...state.selection][0];
        if (ref && scene.notes.some((note) => note.ref === ref)) {
          setEditingNote({ ref });
          return true;
        }
        if (!ref || !scene.nodes.some((node) => node.ref === ref)) return false;
        setEditingLabel(ref);
        return true;
      }
      case "add-note": {
        if (readOnly) return false;
        // Beside a component, attached to it: the one named, else the one selected.
        const only = state.selection.size === 1 ? [...state.selection][0]! : null;
        const anchorRef = command.anchor ?? (command.at ? null : only);
        const node = anchorRef ? scene.nodes.find((candidate) => candidate.ref === anchorRef) : undefined;
        if (!state.annotations.notes) dispatch({ type: "SET_ANNOTATIONS", notes: true });
        if (node) {
          setEditingNote({
            at: { x: node.centre.x + node.size.width / 2 + 16, y: node.centre.y - node.size.height / 2 },
            anchor: node.ref,
          });
        } else {
          setEditingNote({ at: command.at ?? screenToScene(viewport, centre), anchor: null });
        }
        return true;
      }
    }
  };

  // --- Keyboard ---------------------------------------------------------------

  const onKeyDown = (event: ReactKeyboardEvent) => {
    // Keys typed into the label editor are the editor's, not the canvas's:
    // Delete there deletes a character, not the component.
    if ((event.target as Element).closest?.("[data-label-editor]")) return;
    if (event.key === " ") {
      // Held space turns a drag into a pan, as in draw.io and Figma.
      spaceHeld.current = true;
      event.preventDefault();
      return;
    }
    if (event.key === "Escape" && (portCursor || segmentCursor !== null) && !state.drag) {
      clearCursors();
      setAnnouncement("");
      event.preventDefault();
      return;
    }
    // A highlighted port with a connection on it: Tab moves that connection to
    // the next free port on the same component, Shift+Tab to the previous one.
    if (event.key === "Tab" && portCursor && !state.drag && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const holder = connectionAt(state.document, portCursor);
      if (holder) {
        const next = nextFreePort(state.document, state.library, portCursor, event.shiftKey ? -1 : 1);
        if (next) {
          dispatch({ type: "RECONNECT_EDGE", ref: holder.edge, end: holder.end, port: next });
          setPortCursor(next);
          setAnnouncement(`${holder.edge} moved to ${next}`);
        } else {
          setAnnouncement(`No other free port on this component for ${holder.edge}`);
        }
        event.preventDefault();
        return;
      }
    }
    // With a segment chosen, the arrows across it move it one grid step.
    if (segmentCursor !== null && editableEdge) {
      const handle = handles.find((candidate) => candidate.index === segmentCursor);
      const across: Record<string, number> =
        handle?.orientation === "vertical" ? { ArrowLeft: -1, ArrowRight: 1 } : { ArrowUp: -1, ArrowDown: 1 };
      const direction = across[event.key];
      if (handle && direction !== undefined) {
        const step = state.grid.spacing * (event.shiftKey ? 4 : 1) * direction;
        const waypoints = moveSegment(editableEdge, segmentCursor, step, snapValue);
        dispatch({ type: "SET_EDGE_WAYPOINTS", ref: editableEdge.ref, waypoints });
        event.preventDefault();
        return;
      }
    }
    const result = interpretKey(event, state);
    if (result.kind === "none") return;
    const noteKey =
      (result.kind === "action" && result.action.type === "SET_TOOL" && result.action.tool === "note") ||
      (result.kind === "view" && result.command.kind === "add-note");
    if (noteKey && !notes) return;
    if (result.kind === "action") {
      dispatch(result.action);
      event.preventDefault();
      return;
    }
    if (runView(result.command)) event.preventDefault();
  };

  const onKeyUp = (event: ReactKeyboardEvent) => {
    if (event.key === " ") spaceHeld.current = false;
  };

  // --- Pointer ----------------------------------------------------------------

  const capture = (event: ReactPointerEvent) => {
    (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
  };

  // The context menu and label editor render inside the canvas, so their
  // pointer events bubble here. Treated as canvas presses they would clear the
  // selection before a menu item's click could act on it.
  const ownedByOverlay = (event: ReactPointerEvent) =>
    Boolean((event.target as Element).closest?.('[data-label-editor], [role="menu"]'));

  const onPointerDown = (event: ReactPointerEvent) => {
    if (ownedByOverlay(event)) return;
    containerRef.current?.focus();
    // Any click ends choosing ports or segments from the keyboard.
    clearCursors();
    const at = local(event);
    const sceneAt = screenToScene(viewport, at);

    if (event.button === 1 || (event.button === 0 && spaceHeld.current)) {
      gestureRef.current = { kind: "pan", last: at };
      capture(event);
      event.preventDefault();
      return;
    }
    if (event.button !== 0) return;

    // The preview's own controls: a state badge cycles, a port's status opens its editor.
    const toggle = (event.target as Element).closest?.('[data-kind="state-toggle"]');
    if (toggle && stepPreview && onToggleState) {
      onToggleState(toggle.getAttribute("data-ref") ?? "");
      event.preventDefault();
      return;
    }
    const editable = (event.target as Element).closest?.('[data-kind="port-setting"], [data-kind="bag-setting"], [data-kind="regulator-setting"]');
    if (editable && stepPreview && onPreviewEdit) {
      const kinds = { "bag-setting": "bag", "regulator-setting": "regulator" } as const;
      const kind = kinds[editable.getAttribute("data-kind") as keyof typeof kinds] ?? "port";
      onPreviewEdit({ kind, ref: editable.getAttribute("data-ref") ?? "" });
      event.preventDefault();
      return;
    }

    const tolerance = PORT_TOLERANCE / viewport.zoom;

    // An end handle on the selected edge: drag it to another port.
    const endElement = (event.target as Element).closest?.('[data-kind="edge-end"]');
    if (endElement && editableEdge) {
      const end = endElement.getAttribute("data-end") as EdgeEnd;
      setReconnect({
        edge: editableEdge.ref,
        end,
        targets: reconnectTargets(state.document, state.library, editableEdge.ref, end),
        hover: null,
      });
      setPointer(at);
      capture(event);
      return;
    }

    // The corner handle on a selected note: drag it to resize the card.
    const sizeElement = (event.target as Element).closest?.('[data-kind="note-resize"]');
    if (sizeElement) {
      const ref = sizeElement.getAttribute("data-ref") ?? "";
      const note = scene.notes.find((candidate) => candidate.ref === ref);
      if (note) {
        dispatch({ type: "BEGIN_MOVE" });
        gestureRef.current = { kind: "note-size", ref, startScene: sceneAt, start: note.size };
        capture(event);
        event.preventDefault();
        return;
      }
    }

    // A segment handle on the selected edge: drag it across to move the bend.
    const handleElement = (event.target as Element).closest?.('[data-kind="segment"]');
    if (handleElement && editableEdge) {
      const index = Number(handleElement.getAttribute("data-index"));
      const handle = handles.find((candidate) => candidate.index === index);
      if (handle) {
        dispatch({ type: "BEGIN_MOVE" });
        gestureRef.current = { kind: "segment", edge: editableEdge, handle, startScene: sceneAt };
        capture(event);
        return;
      }
    }

    // A connection started from the menu or keyboard ends with a click.
    if (state.drag?.kind === "connect") {
      const port = portAt(scene, sceneAt, tolerance, state.drag.validTargets);
      const line = port ? null : lineUnder(event);
      setDropEdge(null);
      if (line) dispatch({ type: "BRANCH_EDGE", edge: line.ref, from: state.drag.from, at: line.point });
      else dispatch({ type: "COMPLETE_CONNECT", port: port ?? undefined });
      return;
    }

    if (state.tool === "note") {
      // A click writes a note there; on a component, one attached to it; on a
      // note, edits that note.
      const hitNote = (event.target as Element).closest?.('[data-kind="note"]')?.getAttribute("data-ref");
      if (hitNote) {
        dispatch({ type: "SELECT", refs: [hitNote] });
        setEditingNote({ ref: hitNote });
      } else {
        const owner = selectableOf(event.target as Element, scene);
        const anchor = owner && scene.nodes.some((node) => node.ref === owner) ? owner : null;
        if (!state.annotations.notes) dispatch({ type: "SET_ANNOTATIONS", notes: true });
        setEditingNote({ at: sceneAt, anchor });
      }
      event.preventDefault();
      return;
    }

    if (state.tool === "connect") {
      const port = portAt(scene, sceneAt, tolerance);
      if (port) {
        dispatch({ type: "BEGIN_CONNECT", port });
        gestureRef.current = { kind: "connect" };
        setPointer(at);
        capture(event);
        return;
      }
    }

    const target = targetOfElement(event.target as Element);
    const selectable = selectableOf(event.target as Element, scene);
    if (target.kind === "badge" && selectable) {
      // Requirement 12.9: selecting the marker reveals the findings.
      dispatch({ type: "SELECT", refs: [selectable] });
      host.revealFindings?.(selectable);
      return;
    }
    if (selectable) {
      if (event.shiftKey) dispatch({ type: "SELECT", refs: [selectable], mode: "toggle" });
      else if (!state.selection.has(selectable)) dispatch({ type: "SELECT", refs: [selectable] });
    }
    // No pointer capture yet: a press may be a click or a double-click, and
    // capture would retarget those to the canvas instead of what was clicked.
    // It is taken when the press becomes a drag.
    gestureRef.current = { kind: "press", start: at, startScene: sceneAt, onElement: selectable !== null, shift: event.shiftKey };
  };

  const onPointerMove = (event: ReactPointerEvent) => {
    const gesture = gestureRef.current;
    const at = local(event);
    if (reconnect) {
      setPointer(at);
      const hover = portAt(scene, screenToScene(viewport, at), PORT_TOLERANCE / viewport.zoom, reconnect.targets);
      if (hover !== reconnect.hover) setReconnect({ ...reconnect, hover });
      return;
    }
    if (state.drag?.kind === "connect") {
      // Snap the hover to the nearest valid target, and track the pointer for
      // the preview line.
      setPointer(at);
      const hover = portAt(scene, screenToScene(viewport, at), PORT_TOLERANCE / viewport.zoom, state.drag.validTargets);
      if (hover !== state.drag.hover) dispatch({ type: "HOVER_PORT", port: hover });
      // Over a line rather than a port: releasing branches off it.
      const line = hover ? null : (lineUnder(event)?.ref ?? null);
      if (line !== dropEdge) setDropEdge(line);
      return;
    }
    if (!gesture) return;
    const sceneAt = screenToScene(viewport, at);
    switch (gesture.kind) {
      case "pan":
        setViewport(panBy(viewport, at.x - gesture.last.x, at.y - gesture.last.y));
        gestureRef.current = { kind: "pan", last: at };
        return;
      case "press":
        if (Math.hypot(at.x - gesture.start.x, at.y - gesture.start.y) < DRAG_THRESHOLD) return;
        // Now a drag: keep receiving the pointer even outside the canvas.
        containerRef.current?.setPointerCapture?.(event.pointerId);
        if (gesture.onElement && !gesture.shift) {
          dispatch({ type: "BEGIN_MOVE" });
          gestureRef.current = { kind: "move", startScene: gesture.startScene };
          dispatch({ type: "MOVE_SELECTION", dx: sceneAt.x - gesture.startScene.x, dy: sceneAt.y - gesture.startScene.y });
        } else {
          gestureRef.current = { kind: "box", startScene: gesture.startScene, shift: gesture.shift };
          setBand({ a: gesture.startScene, b: sceneAt });
        }
        return;
      case "move": {
        dispatch({ type: "MOVE_SELECTION", dx: sceneAt.x - gesture.startScene.x, dy: sceneAt.y - gesture.startScene.y });
        const line = spliceCandidate() && originScene ? lineIn(originScene, event) : null;
        if ((line?.ref ?? null) !== dropEdge) setDropEdge(line?.ref ?? null);
        return;
      }
      case "box":
        setBand({ a: gesture.startScene, b: sceneAt });
        return;
      case "note-size":
        dispatch({
          type: "SET_NOTE_SIZE",
          ref: gesture.ref,
          size: {
            width: gesture.start.width + sceneAt.x - gesture.startScene.x,
            height: gesture.start.height + sceneAt.y - gesture.startScene.y,
          },
        });
        return;
      case "segment": {
        const offset =
          gesture.handle.orientation === "vertical" ? sceneAt.x - gesture.startScene.x : sceneAt.y - gesture.startScene.y;
        dispatch({
          type: "SET_EDGE_WAYPOINTS",
          ref: gesture.edge.ref,
          waypoints: moveSegment(gesture.edge, gesture.handle.index, offset, snapValue),
        });
        return;
      }
    }
  };

  const onPointerUp = (event: ReactPointerEvent) => {
    if (ownedByOverlay(event) && !gestureRef.current && !reconnect) return;
    if (reconnect) {
      // Dropped away from a valid port: the line stays where it was.
      if (reconnect.hover) {
        dispatch({ type: "RECONNECT_EDGE", ref: reconnect.edge, end: reconnect.end, port: reconnect.hover });
      }
      setReconnect(null);
      setPointer(null);
      return;
    }
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (!gesture) return;
    switch (gesture.kind) {
      case "connect": {
        setPointer(null);
        setDropEdge(null);
        // Released on a line, not a port: a port takes one line, so the new
        // connection joins it through a junction spliced in where it landed.
        const line = state.drag?.kind === "connect" && !state.drag.hover ? lineUnder(event) : null;
        if (line && state.drag?.kind === "connect") {
          dispatch({ type: "BRANCH_EDGE", edge: line.ref, from: state.drag.from, at: line.point });
        } else {
          dispatch({ type: "COMPLETE_CONNECT" });
        }
        return;
      }
      case "move": {
        // Dropped onto a line: splice into it, as a palette drop does.
        const line = spliceCandidate() && originScene ? lineIn(originScene, event) : null;
        setDropEdge(null);
        dispatch(line ? { type: "END_MOVE", intoEdge: line.ref, at: line.point } : { type: "END_MOVE" });
        return;
      }
      case "segment":
      case "note-size":
        dispatch({ type: "END_MOVE" });
        return;
      case "box": {
        const sceneAt = screenToScene(viewport, local(event));
        setBand(null);
        dispatch({ type: "BOX_SELECT", box: boxBetween(gesture.startScene, sceneAt), mode: gesture.shift ? "extend" : "replace" });
        return;
      }
      case "press":
        // A click on empty canvas clears the selection.
        if (!gesture.onElement && !gesture.shift) dispatch({ type: "CLEAR_SELECTION" });
        return;
      case "pan":
        return;
    }
  };

  const onPointerCancel = () => {
    setReconnect(null);
    setDropEdge(null);
    const gesture = gestureRef.current;
    gestureRef.current = null;
    setBand(null);
    setPointer(null);
    if (gesture?.kind === "move" || gesture?.kind === "connect" || gesture?.kind === "segment" || gesture?.kind === "note-size") {
      dispatch({ type: "CANCEL" });
    }
  };

  /**
   * Double-click: on a component, edit its label in place; on a line's
   * dimension, edit its length in place; on a collapsed block, expand it (as
   * draw.io enters a group).
   */
  const onDoubleClick = (event: ReactMouseEvent) => {
    // Read only: nothing to edit in place.
    if (readOnly) return;
    const element = event.target as Element;
    if (element.closest?.("[data-label-editor]")) return;
    const target = targetOfElement(element);
    const owner = selectableOf(element, scene);
    if (!owner) return;
    if (target.kind === "note") {
      dispatch({ type: "SELECT", refs: [owner] });
      setEditingNote({ ref: owner });
      event.preventDefault();
      return;
    }
    if (element.closest?.('[data-label="length"]')) {
      const edgeRef = element.closest("[data-kind=\"edge\"]")?.getAttribute("data-ref");
      if (edgeRef && scene.edges.some((edge) => edge.ref === edgeRef)) {
        dispatch({ type: "SELECT", refs: [edgeRef] });
        setEditingLength(edgeRef);
        event.preventDefault();
        return;
      }
    }
    if (scene.nodes.some((node) => node.ref === owner) && target.kind !== "port") {
      dispatch({ type: "SELECT", refs: [owner] });
      setEditingLabel(owner);
      event.preventDefault();
      return;
    }
    const block = scene.blocks.find((candidate) => candidate.ref === owner);
    if (block?.collapsed) {
      dispatch({ type: "SET_COLLAPSED", instance: owner, collapsed: false });
      event.preventDefault();
    }
  };

  const onContextMenu = (event: ReactMouseEvent) => {
    event.preventDefault();
    const element = event.target as Element;
    // Inside a note being written: the note's text controls.
    if (element.closest?.("[data-note-editor]") && editingNote) {
      openMenu(event.clientX, event.clientY, { kind: "note-text", ref: "ref" in editingNote ? editingNote.ref : null });
      return;
    }
    const hit = targetOfElement(element);
    let target: Target;
    if (hit.kind === "port" && hit.ref) {
      target = hit;
    } else {
      const ref = selectableOf(element, scene);
      target = ref ? { kind: kindOf(ref), ref } : { kind: "canvas", ref: null };
      // As in draw.io: right-clicking outside the selection selects what was
      // hit; inside it, the selection stays so the menu acts on all of it.
      if (ref && !state.selection.has(ref)) dispatch({ type: "SELECT", refs: [ref] });
    }
    openMenu(event.clientX, event.clientY, target);
  };

  // --- Palette drop -----------------------------------------------------------

  /** The line under a drop point, if the component would land on one. */
  const lineIn = (lines: Scene, event: { clientX: number; clientY: number }) =>
    edgeAt(lines, screenToScene(viewport, local(event)), DROP_TOLERANCE / viewport.zoom);
  const lineUnder = (event: { clientX: number; clientY: number }) => lineIn(scene, event);

  const onDragOver = (event: ReactDragEvent) => {
    const types = [...(event.dataTransfer?.types ?? [])];
    if (types.includes(BLOCK_DRAG_TYPE) && host.placeBlock) {
      // A block lands beside lines, never spliced into one.
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      if (dropEdge !== null) setDropEdge(null);
      return;
    }
    if (types.includes(TEMPLATE_DRAG_TYPE)) {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      const line = lineUnder(event)?.ref ?? null;
      if (line !== dropEdge) setDropEdge(line);
    }
  };

  const onDrop = (event: ReactDragEvent) => {
    const blockVersionId = event.dataTransfer?.getData(BLOCK_DRAG_TYPE);
    setDropEdge(null);
    if (blockVersionId && host.placeBlock) {
      event.preventDefault();
      host.placeBlock(blockVersionId, screenToScene(viewport, local(event)));
      containerRef.current?.focus();
      return;
    }
    const templateVersionId = event.dataTransfer?.getData(TEMPLATE_DRAG_TYPE);
    if (!templateVersionId) return;
    event.preventDefault();
    const line = lineUnder(event);
    // Dropped on a line: centre the component on it, and splice it in.
    dispatch(
      line
        ? { type: "PLACE_NODE", templateVersionId, at: line.point, intoEdge: line.ref }
        : { type: "PLACE_NODE", templateVersionId, at: screenToScene(viewport, local(event)) },
    );
    containerRef.current?.focus();
  };

  // --- Drawing ----------------------------------------------------------------

  const highlighted = useMemo(() => {
    if (reconnect) return reconnect.targets;
    if (state.drag?.kind === "connect") return state.drag.validTargets;
    return portCursor ? new Set([portCursor]) : new Set<string>();
  }, [state.drag, portCursor, reconnect]);

  const preview = (() => {
    if (reconnect) {
      // From the end that stays, to where the moving end would land.
      const edge = scene.edges.find((candidate) => candidate.ref === reconnect.edge);
      if (!edge) return null;
      const fixed = reconnect.end === "from" ? edge.to : edge.from;
      const hover = reconnect.hover ? drawnPorts(scene).find((port) => port.ref === reconnect.hover) : undefined;
      const b = hover ? sceneToScreen(viewport, hover.position) : pointer;
      return b ? { a: sceneToScreen(viewport, fixed.point), b, snapped: Boolean(hover) } : null;
    }
    if (state.drag?.kind !== "connect") return null;
    const from = drawnPorts(scene).find((port) => port.ref === (state.drag?.kind === "connect" ? state.drag.from : ""));
    if (!from) return null;
    const hover = state.drag.hover ? drawnPorts(scene).find((port) => port.ref === (state.drag?.kind === "connect" ? state.drag.hover : "")) : undefined;
    const a = sceneToScreen(viewport, from.position);
    const b = hover ? sceneToScreen(viewport, hover.position) : pointer;
    return b ? { a, b, snapped: Boolean(hover) } : null;
  })();

  const bandScreen = band
    ? (() => {
        const a = sceneToScreen(viewport, band.a);
        const b = sceneToScreen(viewport, band.b);
        return { left: Math.min(a.x, b.x), top: Math.min(a.y, b.y), width: Math.abs(a.x - b.x), height: Math.abs(a.y - b.y) };
      })()
    : null;

  const context = {
    state,
    scene,
    host,
    notes,
    noteText: noteEditorRef.current ?? undefined,
    dispatch,
    view: runView,
    connectFrom,
    at: menu ? screenToScene(viewport, local({ clientX: menu.x, clientY: menu.y })) : undefined,
  };

  return (
    <div
      ref={containerRef}
      className={`cx-canvas cx-tool-${state.tool}${state.drag?.kind === "connect" ? " cx-connecting" : ""}`}
      style={{ width, height }}
      tabIndex={0}
      role="application"
      aria-roledescription="schematic editor"
      aria-label={title}
      aria-describedby={instructionsId}
      data-testid="editor-canvas"
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={(event) => {
        // Focus leaving the canvas ends choosing ports or segments; moving into
        // the canvas's own context menu does not.
        if (!containerRef.current?.contains(event.relatedTarget as Node | null)) {
          clearCursors();
          spaceHeld.current = false;
        }
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onContextMenu={onContextMenu}
      onDoubleClick={onDoubleClick}
      onDragOver={onDragOver}
      onDragLeave={(event) => {
        if (!containerRef.current?.contains(event.relatedTarget as Node | null)) setDropEdge(null);
      }}
      onDrop={onDrop}
    >
      <SvgRenderer
        scene={drawnScene}
        width={width}
        height={height}
        viewport={viewport}
        selection={state.selection}
        highlightedPorts={highlighted}
        spotlight={spotlight}
        title={title}
      />
      {preview && (
        <svg className="cx-overlay" width={width} height={height} aria-hidden="true">
          <line
            x1={preview.a.x}
            y1={preview.a.y}
            x2={preview.b.x}
            y2={preview.b.y}
            className={preview.snapped ? "cx-preview cx-preview-snapped" : "cx-preview"}
          />
        </svg>
      )}
      {handles.length > 0 || editableEdge ? (
        <svg className="cx-overlay" width={width} height={height} aria-hidden="true">
          {editableEdge &&
            !reconnect &&
            (["from", "to"] as const).map((end) => {
              const point = editableEdge[end];
              if (point.concealed) return null;
              const at = sceneToScreen(viewport, point.point);
              return (
                <circle
                  key={end}
                  data-kind="edge-end"
                  data-end={end}
                  className="cx-edge-end"
                  cx={at.x}
                  cy={at.y}
                  r={6}
                />
              );
            })}
          {handles.map((handle) => {
            const at = sceneToScreen(viewport, handle.point);
            return (
              <rect
                key={handle.index}
                data-kind="segment"
                data-index={handle.index}
                className={`cx-segment cx-segment-${handle.orientation}${handle.index === segmentCursor ? " cx-segment-active" : ""}`}
                x={at.x - 4}
                y={at.y - 4}
                width={8}
                height={8}
              />
            );
          })}
        </svg>
      ) : null}
      {sizingNote &&
        (() => {
          const corner = sceneToScreen(viewport, {
            x: sizingNote.position.x + sizingNote.size.width,
            y: sizingNote.position.y + sizingNote.size.height,
          });
          return (
            <svg className="cx-overlay" width={width} height={height} aria-hidden="true">
              <rect
                data-kind="note-resize"
                data-ref={sizingNote.ref}
                className="cx-note-resize"
                x={corner.x - 5}
                y={corner.y - 5}
                width={10}
                height={10}
              />
            </svg>
          );
        })()}
      {previewEdit &&
        stepPreview &&
        renderPreviewEdit &&
        (() => {
          const node = scene.nodes.find((candidate) => candidate.ref === previewEdit.ref);
          if (!node) return null;
          const at = sceneToScreen(
            viewport,
            previewEdit.kind === "port" || previewEdit.kind === "regulator"
              ? portStatusAt(node, state.annotations.labels)
              : { x: node.centre.x, y: node.centre.y + node.size.height / 2 + 4 },
          );
          return renderPreviewEdit(previewEdit, at, () => {
            onPreviewEdit?.(null);
            containerRef.current?.focus();
          });
        })()}
      {editingLabel &&
        (() => {
          const node = scene.nodes.find((candidate) => candidate.ref === editingLabel);
          const documentNode = state.document.nodes?.find((candidate) => candidate.local_id === editingLabel);
          if (!node || !documentNode) return null;
          const placed = labelPlacement(node.centre, node.size, node.rotation, node.labelSide);
          const baseline = sceneToScreen(viewport, placed);
          return (
            <LabelEditor
              key={editingLabel}
              initial={documentNode.label ?? ""}
              placeholder={editingLabel}
              x={baseline.x}
              y={baseline.y}
              zoom={viewport.zoom}
              onCommit={(label) => {
                dispatch({ type: "SET_LABEL", ref: editingLabel, label });
                setEditingLabel(null);
                containerRef.current?.focus();
              }}
              onCancel={() => {
                setEditingLabel(null);
                containerRef.current?.focus();
              }}
            />
          );
        })()}
      {editingLength &&
        (() => {
          const drawn = scene.edges.find((candidate) => candidate.ref === editingLength);
          const edge = state.document.edges?.find((candidate) => candidate.local_id === editingLength);
          if (!drawn || !edge) return null;
          const template = edge.template_version_id ? state.library.templates[edge.template_version_id] : undefined;
          const row = effectiveParameters(edge, template).find((candidate) => candidate.key === "length");
          const length = edge.hydraulic?.length ?? null;
          const unit = liquidUnits?.length ?? lengthLabelUnit(length);
          // Where the dimension is drawn: 6 above the label point, as the renderer does.
          const at = sceneToScreen(viewport, { x: drawn.labelPosition.x, y: drawn.labelPosition.y - 6 });
          const close = () => {
            setEditingLength(null);
            containerRef.current?.focus();
          };
          return (
            <LengthEditor
              key={editingLength}
              edgeRef={editingLength}
              initial={length}
              unit={unit}
              x={at.x}
              y={at.y}
              zoom={viewport.zoom}
              check={(metres) => {
                if (!(metres > 0)) return "A length must be more than zero";
                return row ? (validateParameter(editingLength, row.declaration, metres)?.message ?? null) : null;
              }}
              onCommit={(metres) => {
                dispatch({ type: "EDIT_PARAMETERS", refs: [editingLength], key: "length", value: metres });
                close();
              }}
              onCancel={close}
            />
          );
        })()}
      {editingNote &&
        (() => {
          const existing =
            "ref" in editingNote ? state.document.text_notes?.find((note) => note.local_id === editingNote.ref) : undefined;
          if ("ref" in editingNote && !existing) return null;
          const corner = sceneToScreen(viewport, existing ? existing.position : (editingNote as { at: Point }).at);
          // Opens at the card's own size: as the designer left it, or as its text laid it out.
          const drawn = existing ? scene.notes.find((note) => note.ref === existing.local_id) : undefined;
          const size = drawn?.size ?? existing?.size ?? { width: NOTE_METRICS.autoWidth, height: heightFor(1) };
          const close = () => {
            setEditingNote(null);
            containerRef.current?.focus();
          };
          return (
            <NoteEditor
              ref={noteEditorRef}
              key={"ref" in editingNote ? editingNote.ref : `new-${corner.x}-${corner.y}`}
              initial={existing?.text ?? ""}
              label={existing ? `Text of ${existing.local_id}` : "New note"}
              describedBy={noteHelpId}
              x={corner.x}
              y={corner.y}
              size={size}
              zoom={viewport.zoom}
              onCommit={(text, kept) => {
                if (existing) dispatch({ type: "SET_NOTE_TEXT", ref: existing.local_id, text, size: kept });
                else if ("at" in editingNote)
                  dispatch({ type: "ADD_NOTE", at: editingNote.at, text, anchor: editingNote.anchor, size: kept });
                close();
              }}
              onCancel={close}
            />
          );
        })()}
      <div id={noteHelpId} className="visually-hidden">
        Command or Control with Enter, or leaving the note, saves it; Escape cancels. Enter starts a new line.
        Command or Control with B, I and U make text bold, italic and underlined, with Shift and X struck
        through; with Shift and 8 or 7, a bulleted or numbered list. The menu has the same controls. Drag the
        corner to resize the note. An emptied note is deleted.
      </div>
      {dropEdge && (
        <svg className="cx-overlay" width={width} height={height} aria-hidden="true">
          <polyline
            className="cx-drop-target"
            data-drop-target={dropEdge}
            points={((originScene ?? scene).edges.find((edge) => edge.ref === dropEdge)?.path ?? [])
              .map((point) => sceneToScreen(viewport, point))
              .map((point) => `${point.x},${point.y}`)
              .join(" ")}
          />
        </svg>
      )}
      {bandScreen && <div className="cx-band" style={bandScreen} aria-hidden="true" />}
      {state.notice && (
        <div className={`cx-notice cx-notice-${state.notice.kind}`}>
          <span aria-hidden="true">{state.notice.message}</span>
          <button
            type="button"
            className="cx-notice-close"
            aria-label="Dismiss notice"
            title="Dismiss"
            // Not the canvas's gesture: no box selection starts here.
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => {
              dispatch({ type: "CLEAR_NOTICE" });
              containerRef.current?.focus();
            }}
          >
            ×
          </button>
        </div>
      )}
      <div className="visually-hidden" role="status" aria-live="polite">
        {announcement}
      </div>
      <div id={instructionsId} className="visually-hidden">
        Tab and Shift+Tab move between elements. Arrow keys move the selection. V selects, C connects: with a
        component selected, P chooses a port and Enter starts a connection, then P chooses a target and Enter
        connects. F2 or a double-click edits a component's label or a note. N is the note tool: click to write a note, on a component to attach it; with the note tool, Enter writes one beside the selected component. With a channel selected, B chooses a segment and the arrow keys move its bend; P chooses one of its ends, and Tab moves that end to the next free port on the same component; J inserts a junction so another channel can join it. Dragging a connection onto a channel branches it the same way. R rotates, H flips, Delete deletes, Control or Command with X, C and V cuts, copies and pastes, and with Z undoes. Plus and minus zoom,
        Shift+1 fits, and arrow keys pan when nothing is selected. Shift+F10 opens the menu for the selection.
      </div>
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          label={
            menu.target.kind === "note-text"
              ? "Text formatting"
              : menu.target.ref
                ? `Actions for ${menu.target.ref}`
                : "Canvas actions"
          }
          entries={menuCommands(menu.target, context)}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}
